/**
 * Client-side logger: sends app activity to the backend so you can see
 * Android (and optional web) actions in AWS terminal via pm2 logs.
 * Crash reports are sent separately via crashReporter.js.
 */

// const getApiBase = () =>
//   process.env.REACT_APP_API_BASE_URL || process.env.REACT_APP_API_URL || 'https://restroai.in/api';

const getApiBase = () =>
  process.env.REACT_APP_API_BASE_URL || process.env.REACT_APP_API_URL || 'https://restro-backend-vercel.vercel.app/api';

const isNative = () => {
  if (typeof window === 'undefined') return false;
  return window.Capacitor?.isNativePlatform?.() === true;
};

const shouldSend = () => {
  if (typeof window === 'undefined') return false;
  // Always send from native app (Android/iOS)
  if (isNative()) return true;
  // From web, only if explicitly enabled (e.g. for debugging)
  return process.env.REACT_APP_LOG_CLIENT === 'true' || process.env.REACT_APP_LOG_CLIENT === '1';
};

const getPlatform = () => {
  if (typeof window === 'undefined') return 'web';
  try {
    if (window.Capacitor?.getPlatform) return window.Capacitor.getPlatform();
  } catch (_) {}
  return window.navigator?.userAgent?.toLowerCase().includes('android') ? 'android' : 'web';
};

const getAppVersion = () => {
  try {
    const Cap = window.Capacitor;
    if (Cap?.getApp && typeof Cap.getApp === 'function') {
      return Cap.getApp()
        .then((app) => (app && app.getInfo ? app.getInfo() : null))
        .then((info) => (info && info.version ? info.version : null))
        .catch(() => null);
    }
  } catch (_) {}
  return Promise.resolve(null);
};

const getUserId = () => {
  try {
    const userData = localStorage.getItem('userData');
    if (userData) {
      const u = JSON.parse(userData);
      return u?.id || u?.userId || null;
    }
  } catch (_) {}
  return null;
};

// Throttle: max one request per 500ms for the same message type to avoid flooding
let lastSend = 0;
const MIN_INTERVAL = 500;

function sendToBackend(body) {
  if (!shouldSend()) return;
  const now = Date.now();
  if (now - lastSend < MIN_INTERVAL) {
    // Still send, but could queue; for simplicity we send (backend can handle rate)
  }
  lastSend = now;

  const url = `${getApiBase()}/logs/client`;
  getAppVersion().then((appVersion) => {
    const payload = {
      ...body,
      platform: getPlatform(),
      appVersion: appVersion || undefined,
      userId: getUserId(),
      timestamp: new Date().toISOString(),
    };
    fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    }).catch(() => {});
  });
}

/**
 * Log an action or message from the app. Shown in AWS terminal when you run: pm2 logs
 * @param {string} message - Short description (e.g. "App opened", "Login success")
 * @param {object} options - { action, screen, payload }
 */
export function log(message, options = {}) {
  const { action, screen, payload = {} } = options;
  sendToBackend({
    level: 'info',
    message,
    action: action || message,
    screen,
    payload,
  });
}

/**
 * Log current screen view (call when entering a main screen).
 * Use in route change or at top of dashboard/screen components.
 */
export function screen(screenName, extra = {}) {
  sendToBackend({
    level: 'info',
    message: `screen: ${screenName}`,
    action: 'screen_view',
    screen: screenName,
    payload: extra,
  });
}

/**
 * Log an error/warning from the client (non-fatal). For fatal crashes use crashReporter.
 */
export function error(message, payload = {}) {
  sendToBackend({
    level: 'error',
    message,
    action: 'client_error',
    payload,
  });
}

const appLogger = { log, screen, error, isNative: isNative(), shouldSend: shouldSend() };
export default appLogger;
